import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Create the context
const UserContext = createContext();

// Custom hook to access the UserContext
export const useUser = () => useContext(UserContext);

export const UserProvider = ({ children }) => {
  const navigate = useNavigate();

  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });

  const [userAuthToken, setUserAuthToken] = useState(() => {
    return localStorage.getItem('userAuthToken') || null;
  });

  const [tokenExpired, setTokenExpired] = useState(false);

  // Listen for localStorage changes across tabs and explicit user-updated events
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        setUser(e.newValue ? JSON.parse(e.newValue) : null);
      }
      if (e.key === 'userAuthToken') {
        setUserAuthToken(e.newValue || null);
      }
    };

    const handleUserUpdated = (e) => {
      const detail = e?.detail || {};
      if (detail.user) {
        setUser(detail.user);
        localStorage.setItem('user', JSON.stringify(detail.user));
      }
      if (detail.token) {
        setUserAuthToken(detail.token);
        localStorage.setItem('userAuthToken', detail.token);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('user-updated', handleUserUpdated);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('user-updated', handleUserUpdated);
    };
  }, []);

  // Check token expiration every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!userAuthToken) return;

      try {
        const [, payloadBase64] = userAuthToken.split('.');
        const decodedPayload = JSON.parse(atob(payloadBase64));
        const isExpired = decodedPayload.exp * 1000 < Date.now();

        if (isExpired) {
          setTokenExpired(true);
          handleLogout();
        } else {
          setTokenExpired(false);
        }
      } catch (error) {
        console.error("Token parsing failed", error);
        setTokenExpired(true);
        handleLogout();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [userAuthToken]);

  const isCustomerTokenExpired = () => {
    if (!userAuthToken) return true;

    try {
      const [, payloadBase64] = userAuthToken.split('.');
      const decodedPayload = JSON.parse(atob(payloadBase64));
      return decodedPayload.exp * 1000 < Date.now();
    } catch {
      return true; // assume expired if error
    }
  };

  const updateUser = (newUser, token = null) => {
    const updatedUser = { ...user, ...newUser };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));

    if (token) {
      setUserAuthToken(token);
      localStorage.setItem('userAuthToken', token);
    }
  };

  const handleLogout = () => {
    setUser(null);
    setUserAuthToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('userAuthToken');
    navigate('/login');
  };

  const logoutDir = () => {
    setUser(null);
    setUserAuthToken(null);
    localStorage.removeItem('user');
    localStorage.removeItem('userAuthToken');
  };

  const getJwtToken = () => userAuthToken;

  return (
    <UserContext.Provider
      value={{
        user,
        userAuthToken,
        updateUser,
        getJwtToken,
        logout: handleLogout,
        logoutDir,
        isCustomerTokenExpired,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
